import React, { useEffect, useState } from 'react';
import { decryptBytes } from '../utils/crypto';
import type { UserType } from '../types';
import HiddenIcon from '../img/datos_ocultos.png';
import VisibleIcon from '../img/datos_visibles.png';
import TrashClosed from '../img/papelera cerrada.png';
import TrashOpen from '../img/papelera abierta.png';
import CopyIcon from '../img/copiar.png';
import '../styles/ControlPanel.css';
import { encryptBytes } from '../utils/crypto';
// Definir URL fija para gestión de usuarios
const USERS_URL = 'https://headspringchatbot.azurewebsites.net/api/usuarios_conversaciones?code=AtlU_yuKGwLNPV7igGaqgtHylvaaqh6klALNXL_ZJJKvAzFuzYjyyw==';

// Mover la función de traducción fuera del componente
const t = (lang: 'es' | 'en', es: string, en: string) => (lang === 'es' ? es : en);
// Generar un código de acceso aleatorio de 5 dígitos
const generateAccessCode = () => (Math.floor(Math.random() * 90000) + 10000).toString();
// URL fija para listado de programas
const PROGRAMS_URL = 'https://headspringchatbot.azurewebsites.net/api/procesar_conversaciones?code=qI6DmJpMP3tLLLnjOy0gXjxIwTKKfJzLogsQKIopL0voAzFuit0o9w==&accion=get_asistentes';

interface ControlPanelProps {
  token: string;
  onBack: () => void;
  currentLang: 'es' | 'en';
  onLangChange: (lang: 'es' | 'en') => void;
}

const ControlPanel: React.FC<ControlPanelProps> = ({ token, onBack, currentLang, onLangChange }) => {
  // Control mostrar/ocultar clave por fila
  const [showPasswords, setShowPasswords] = useState<Set<number>>(new Set());
  const toggleShow = (index: number) => {
    setShowPasswords(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado para modal de añadir usuario
  const [showModal, setShowModal] = useState(false);
  const [newCorreo, setNewCorreo] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAdmin, setNewAdmin] = useState(false);
  // Programas disponibles y seleccionados
  const [programOptions, setProgramOptions] = useState<string[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // Handler para abrir el modal y generar código de acceso
  const handleOpenAddModal = () => {
    setNewCorreo('');
    setNewPassword(generateAccessCode());
    setSelectedPrograms([]);
    setNewAdmin(false);
    setAddError(null);
    setShowModal(true);
  };
  // Cargar programas al montar
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(PROGRAMS_URL);
        const text = await res.text();
        if (!res.ok) throw new Error(text);
        const data = JSON.parse(text);
        const activos = data.programas_activos || [];
        const inactivos = data.programas_no_activos || [];
        setProgramOptions([...activos, ...inactivos]);
      } catch (err) {
        console.error('Error cargando programas:', err);
      }
    };
    fetchPrograms();
  }, []);

  // Estados para edición de usuario
  const [showEditModal, setShowEditModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editCorreo, setEditCorreo] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editCodigoPrograma, setEditCodigoPrograma] = useState<string[]>([]);
  const [editAdmin, setEditAdmin] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Estados para eliminar usuario
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Abrir modal de edición con datos del usuario seleccionado
  const handleEdit = (index: number) => {
    const user = users[index];
    setEditIndex(index);
    setEditCorreo(user.correo);
    setEditPassword(user.password);
    setEditCodigoPrograma(user.codigo_programa);
    setEditAdmin(user.admin);
    setEditError(null);
    setShowEditModal(true);
  };

  // Abrir modal de eliminación
  const handleDelete = (index: number) => {
    setDeleteIndex(index);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  // Confirmar eliminación
  const confirmDelete = async () => {
    if (deleteIndex === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const userToDelete = users[deleteIndex];
      const payload = JSON.stringify({ correo: userToDelete.correo, password: userToDelete.password });
      const userToken = encryptBytes(payload, process.env.REACT_APP_FERNET_KEY!);
      const url = USERS_URL;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: userToken, accion: 'eliminar_usuario' }),
      });
      const textResp = await res.text();
      if (!res.ok) {
        throw new Error(textResp);
      }
      // Desencriptar respuesta
      const data = JSON.parse(decryptBytes(textResp, process.env.REACT_APP_FERNET_KEY!));
      if (!data.result) throw new Error(t(currentLang, 'No se pudo eliminar el usuario', 'Could not delete user'));
      setUsers(prev => prev.filter((_, i) => i !== deleteIndex));
      setShowDeleteModal(false);
    } catch (err: any) {
      console.error(err);
      setDeleteError(err.message || t(currentLang, 'Error al eliminar usuario', 'Error deleting user'));
    } finally {
      setDeleting(false);
    }
  };

  // Guardar cambios de usuario
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editIndex === null) return;
    setEditLoading(true);
    setEditError(null);
    try {
      const oldCorreo = users[editIndex].correo;
      const oldPassword = users[editIndex].password;
      // generar token con credenciales antiguas
      const payload = JSON.stringify({ correo: oldCorreo, password: oldPassword });
      const userToken = encryptBytes(payload, process.env.REACT_APP_FERNET_KEY!);
      const url = USERS_URL;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: userToken, accion: 'modificar_usuario', nuevo_correo: editCorreo, nuevo_password: editPassword, nuevo_codigo_programas: editCodigoPrograma, admin: editAdmin }),
      });
      const textResp = await res.text();
      if (!res.ok) {
        throw new Error(textResp);
      }
      // Desencriptar respuesta
      const data = JSON.parse(decryptBytes(textResp, process.env.REACT_APP_FERNET_KEY!));
      if (!data.result) throw new Error(t(currentLang, 'No se pudo modificar el usuario', 'Could not modify user'));
      // actualizar lista localmente
      setUsers(prev => {
        const next = [...prev];
        next[editIndex] = { correo: editCorreo, password: editPassword, codigo_programa: editCodigoPrograma, admin: editAdmin };
        return next;
      });
      setShowEditModal(false);
    } catch (err: any) {
      console.error(err);
      setEditError(err.message || t(currentLang, 'Error al modificar usuario', 'Error modifying user'));
    } finally {
      setEditLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = USERS_URL;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accion: 'listar_usuarios' }),
        });
        const text = await res.text();
        if (!res.ok) throw new Error(text);
        const decrypted = decryptBytes(text, process.env.REACT_APP_FERNET_KEY!);
        const parsedJson: any = JSON.parse(decrypted);
        // Extraer array de usuarios: si viene como { result: [...] }
        let rawList: any[];
        if (Array.isArray(parsedJson)) {
          rawList = parsedJson;
        } else if (parsedJson.result && Array.isArray(parsedJson.result)) {
          rawList = parsedJson.result;
        } else {
          rawList = [parsedJson];
        }
        const parsed: UserType[] = rawList.map(u => {
          let codes: string[] = [];
          if (Array.isArray(u.codigo_programa)) {
            codes = u.codigo_programa;
          } else if (typeof u.codigo_programa === 'string') {
            const raw = u.codigo_programa;
            // Si parece un JSON de array, parsear directamente
            if (raw.trim().startsWith('[') && raw.trim().endsWith(']')) {
              try {
                const arr = JSON.parse(raw);
                codes = Array.isArray(arr) ? arr : [raw];
              } catch {
                codes = [raw];
              }
            } else {
              codes = [raw];
            }
          }
          return {
            correo: u.correo,
            password: u.password,
            codigo_programa: codes,
            admin: u.admin,
          };
        });
        setUsers(parsed);
      } catch (err: any) {
        console.error(err);
        setError(err.message || t(currentLang, 'Error al listar usuarios', 'Error listing users'));
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [token, currentLang]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      // Encriptar credenciales del nuevo usuario
      const payload = JSON.stringify({ correo: newCorreo, password: newPassword });
      const userToken = encryptBytes(payload, process.env.REACT_APP_FERNET_KEY!);
      // Usar USERS_URL para añadir usuario
      const url = USERS_URL;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: userToken, accion: 'añadir_usuario', codigo_programa: selectedPrograms, admin: newAdmin }),
      });
      const textResp = await res.text();
      if (!res.ok) {
        throw new Error(textResp);
      }
      // Desencriptar respuesta
      const data = JSON.parse(decryptBytes(textResp, process.env.REACT_APP_FERNET_KEY!));
      if (!data.result) throw new Error(t(currentLang, 'No se pudo añadir el usuario', 'Could not add user'));
      // cerrar modal y recargar lista
      setShowModal(false);
      // refrescar tabla
      setUsers(prev => [...prev, { correo: newCorreo, password: newPassword, codigo_programa: selectedPrograms, admin: newAdmin }]);
    } catch (err: any) {
      console.error(err);
      setAddError(err.message || t(currentLang, 'Error al añadir usuario', 'Error adding user'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="control-page">
      <div className="control-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button className="back-btn" onClick={onBack}>{t(currentLang, 'Volver', 'Back')}</button>
        <h2>{t(currentLang, 'Panel de Control - Usuarios', 'Control Panel - Users')}</h2>
      </div>
      <button
        className="add-btn"
        onClick={handleOpenAddModal}
        aria-label={t(currentLang, 'Añadir usuario', 'Add user')}
      >
        +
      </button>
      {loading ? (
        <p>{t(currentLang, 'Cargando usuarios...', 'Loading users...')}</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t(currentLang, 'Correo', 'Email')}</th>
                <th>{t(currentLang, 'Código de Acceso', 'Access Code')}</th>
                <th>{t(currentLang, 'Código Programa', 'Program Code')}</th>
                <th>{t(currentLang, 'Admin', 'Admin')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i}>
                  <td onClick={() => handleEdit(i)}>{u.correo}</td>
                  <td onClick={() => handleEdit(i)}>
                    {showPasswords.has(i) ? u.password : '••••••'}
                    <button
                      className="show-pass-btn"
                      onClick={e => { e.stopPropagation(); toggleShow(i); }}
                      aria-label={showPasswords.has(i) ? t(currentLang, 'Ocultar código de acceso', 'Hide access code') : t(currentLang, 'Mostrar código de acceso', 'Show access code')}
                    >
                      <img
                        src={showPasswords.has(i) ? HiddenIcon : VisibleIcon}
                        alt={showPasswords.has(i) ? t(currentLang, 'Oculto', 'Hidden') : t(currentLang, 'Visible', 'Visible')}
                      />
                    </button>
                    <button
                      className="copy-pass-btn"
                      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(u.password); }}
                      aria-label={t(currentLang, 'Copiar código', 'Copy code')}
                    >
                      <img src={CopyIcon} alt={t(currentLang, 'Copiar código', 'Copy code')} />
                    </button>
                  </td>
                  <td onClick={() => handleEdit(i)}>
                    {u.codigo_programa.map(code => (
                      <span className="code-tag" key={code}>
                        {code}
                      </span>
                    ))}
                  </td>
                  <td onClick={() => handleEdit(i)}>{u.admin ? t(currentLang, 'Sí', 'Yes') : t(currentLang, 'No', 'No')}</td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={e => { e.stopPropagation(); handleDelete(i); }}
                      aria-label={t(currentLang, 'Eliminar usuario', 'Delete user')}
                    >
                      <img src={TrashClosed} alt="Delete" className="trash-closed" />
                      <img src={TrashOpen} alt="Delete" className="trash-open" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{t(currentLang, 'Agregar Usuario', 'Add User')}</h3>
            <form onSubmit={handleAdd}>
              <div>
                <label>{t(currentLang, 'Correo:', 'Email:')}</label>
                <input type="email" value={newCorreo} onChange={e => setNewCorreo(e.target.value)} required />
              </div>
              <div>
                <label>{t(currentLang, 'Código de acceso:', 'Access Code:')}</label>
                <input type="password" value={newPassword} readOnly />
              </div>
              <div className="tag-selector">
                <label>{t(currentLang, 'Códigos de Programa:', 'Program Codes:')}</label>
                <div className="tags">
                  {selectedPrograms.map((code, idx) => (
                    <span className="tag" key={idx}>
                      {code}
                      <button type="button" className="remove-tag" onClick={() => setSelectedPrograms(prev => prev.filter(c => c !== code))}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <select onChange={e => {
                    const v = e.target.value;
                    if (v && !selectedPrograms.includes(v)) {
                      setSelectedPrograms(prev => [...prev, v]);
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">{t(currentLang, 'Selecciona...', 'Select...')}</option>
                  {programOptions.filter(code => !selectedPrograms.includes(code)).map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>{t(currentLang, 'Admin:', 'Admin:')}</label>
                <select value={newAdmin ? 'true' : 'false'} onChange={e => setNewAdmin(e.target.value === 'true')}>
                  <option value="false">{t(currentLang, 'No', 'No')}</option>
                  <option value="true">{t(currentLang, 'Sí', 'Yes')}</option>
                </select>
              </div>
              {addError && <p className="error">{addError}</p>}
              <div className="btn-group">
                <button type="button" onClick={() => setShowModal(false)}>{t(currentLang, 'Cancelar', 'Cancel')}</button>
                <button type="submit" disabled={adding}>{adding ? t(currentLang, 'Guardando...', 'Saving...') : t(currentLang, 'Guardar', 'Save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{t(currentLang, 'Modificar Usuario', 'Modify User')}</h3>
            <form onSubmit={handleUpdate}>
              <div>
                <label>{t(currentLang, 'Correo:', 'Email:')}</label>
                <input type="email" value={editCorreo} onChange={e => setEditCorreo(e.target.value)} required />
              </div>
              <div>
                <label>{t(currentLang, 'Código de acceso:', 'Access Code:')}</label>
                <input type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} required />
              </div>
              <div className="tag-selector">
                <label>{t(currentLang, 'Códigos de Programa:', 'Program Codes:')}</label>
                <div className="tags">
                  {editCodigoPrograma.map((code, idx) => (
                    <span className="tag" key={idx}>
                      {code}
                      <button type="button" className="remove-tag" onClick={() => setEditCodigoPrograma(prev => prev.filter(c => c !== code))}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <select onChange={e => {
                    const v = e.target.value;
                    if (v && !editCodigoPrograma.includes(v)) {
                      setEditCodigoPrograma(prev => [...prev, v]);
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">{t(currentLang, 'Selecciona...', 'Select...')}</option>
                  {programOptions.filter(code => !editCodigoPrograma.includes(code)).map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>{t(currentLang, 'Admin:', 'Admin:')}</label>
                <select value={editAdmin ? 'true' : 'false'} onChange={e => setEditAdmin(e.target.value === 'true')}>
                  <option value="false">{t(currentLang, 'No', 'No')}</option>
                  <option value="true">{t(currentLang, 'Sí', 'Yes')}</option>
                </select>
              </div>
              {editError && <p className="error">{editError}</p>}
              <div className="btn-group">
                <button type="button" onClick={() => setShowEditModal(false)}>{t(currentLang, 'Cancelar', 'Cancel')}</button>
                <button type="submit" disabled={editLoading}>{editLoading ? t(currentLang, 'Guardando...', 'Saving...') : t(currentLang, 'Guardar', 'Save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{t(currentLang, 'Confirmar Eliminación', 'Confirm Deletion')}</h3>
            <p>{t(currentLang, '¿Estás seguro de que quieres eliminar a este usuario?', 'Are you sure you want to delete this user?')}</p>
            {deleteError && <p className="error">{deleteError}</p>}
            <div className="btn-group">
              <button type="button" onClick={() => setShowDeleteModal(false)}>{t(currentLang, 'Cancelar', 'Cancel')}</button>
              <button onClick={confirmDelete} disabled={deleting} className="confirm-delete-btn">
                {deleting ? t(currentLang, 'Eliminando...', 'Deleting...') : t(currentLang, 'Sí, eliminar', 'Yes, delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel; 